/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : StimulationFileIndex.jsx
 * Version        : 1.1.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Grid } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CommonBreadcrumbs from "../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import ProjectvariantSearchbar from "../../../components/customComponentsV2/common/projectSearchbar/ProjectvariantSearchbar";
import ProjectVariantDialog from "../../../components/customComponentsV2/vlc/ProjectVarientAccordian/ProjectVariantDialog";
import TicketsTable from "../../regulatory-compliance/intersections/TicketsTable";
import { TICKETS_TYPES } from "../../../config/appConstants";
import { HandleApiActions } from "../../../redux/actions";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import { useDispatch, useSelector } from "react-redux";
import TicketsFilter from "../../regulatory-compliance/tickets/TicketsFilter";
import { useSearchParams } from "react-router-dom";
import CustomTabsV2 from "../../../components/customComponentsV2/common/tabs/CustomTabsV2";

function StimulationFileIndex() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const tabFromUrl = Number.parseInt(searchParams.get("tab"), 10) || 0;
  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariant = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  const [selectedTicketType, setSelectedTicketType] = useState(null);

  const [ticketDetails, setTicketDetails] = useState([]);
  const [selectedIntersectionIds, setSelectedIntersectionIds] = useState([]);
  const [page] = useState(1);
  const [rowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [filterData, setFilterData] = useState({ archive_status: "ACTIVE" });

  const memoizedProjectVariant = useMemo(
    () => ({
      projectId: selectedProject?.project_id,
      variantId: selectedVariant?.variant_id,
    }),
    [selectedProject?.project_id, selectedVariant?.variant_id],
  );

  const fetchTickets = useCallback(
    async (requestObj = {}) => {
      if (selectedTicketType) {
        const resp = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...typeApprovalAPI?.get_tickets,
              params: {
                project_id: memoizedProjectVariant.projectId,
                variant_id: memoizedProjectVariant.variantId,
                page_size: rowsPerPage,
                page_number: page,
                tickets_type: selectedTicketType,
                archive_status: "ACTIVE",
                ...filterData,
                ...requestObj,
              },
              show_toast: false,
            }),
          ),
        );
        if (resp.error) {
          setTicketDetails([]);
        } else {
          setTicketDetails(resp.data);
          setTotalCount(resp?.total_count);
        }
      }
    },
    [
      dispatch,
      memoizedProjectVariant.projectId,
      memoizedProjectVariant.variantId,
      rowsPerPage,
      page,
      selectedTicketType,
      filterData,
    ],
  );

  useEffect(() => {
    if (memoizedProjectVariant.projectId && memoizedProjectVariant.variantId) {
      fetchTickets();
    }
  }, [memoizedProjectVariant, fetchTickets]);

  const handleTabChange = (tabIndex) => {
    const ticketTypes = [
      TICKETS_TYPES.VEHICLE_SYSTEM,
      TICKETS_TYPES.VEHICLE_COMPONENT,
      TICKETS_TYPES.EE,
      TICKETS_TYPES.SWM,
    ];
    setSelectedTicketType(ticketTypes[tabIndex]);
    setTicketDetails([]);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container>
        <Grid item sm={12} sx={{ p: 1 }}>
          <CommonBreadcrumbs
            items={[
              { label: "Document Library" },
              { label: "Simulation File" },
            ]}
          />
        </Grid>
        <Grid item sm={12}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box>
              <TicketsFilter
                getFilterDetails={(data) => {
                  setFilterData(data);
                }}
              />
            </Box>
            <Box>
              <ProjectvariantSearchbar />
            </Box>

            <ProjectVariantDialog />
          </Box>
        </Grid>
        <Grid item sm={12}>
          <CustomTabsV2
            defaultTab={tabFromUrl}
            onTabChange={handleTabChange}
            centerTabs={true}
            tabs={[
              {
                name: "Vehicle System",
                count: "",
                content: (
                  <TicketsTable
                    tickets_type={TICKETS_TYPES.VEHICLE_SYSTEM}
                    ticketDetails={ticketDetails}
                    selectedTicketType={selectedTicketType}
                    setSelectedTicketType={setSelectedTicketType}
                    selectedIntersectionIds={selectedIntersectionIds}
                    setSelectedIntersectionIds={setSelectedIntersectionIds}
                    totalCount={totalCount}
                    fetchTickets={fetchTickets}
                    enableAction={true}
                    showPagination={true}
                    enableSimulation={true}
                    parentPage="simulations"
                  />
                ),
              },
              {
                name: "Vehicle Component",
                count: "",
                content: (
                  <TicketsTable
                    tickets_type={TICKETS_TYPES.VEHICLE_COMPONENT}
                    ticketDetails={ticketDetails}
                    selectedTicketType={selectedTicketType}
                    setSelectedTicketType={setSelectedTicketType}
                    selectedIntersectionIds={selectedIntersectionIds}
                    setSelectedIntersectionIds={setSelectedIntersectionIds}
                    totalCount={totalCount}
                    fetchTickets={fetchTickets}
                    enableAction={true}
                    showPagination={true}
                    enableSimulation={true}
                    parentPage="simulations"
                  />
                ),
              },
              {
                name: "EE Component",
                count: "",
                content: (
                  <TicketsTable
                    tickets_type={TICKETS_TYPES.EE}
                    ticketDetails={ticketDetails}
                    selectedTicketType={selectedTicketType}
                    setSelectedTicketType={setSelectedTicketType}
                    selectedIntersectionIds={selectedIntersectionIds}
                    setSelectedIntersectionIds={setSelectedIntersectionIds}
                    totalCount={totalCount}
                    fetchTickets={fetchTickets}
                    enableAction={true}
                    showPagination={true}
                    enableSimulation={true}
                    parentPage="simulations"
                  />
                ),
              },
              {
                name: "Software Module",
                count: "",
                content: (
                  <TicketsTable
                    tickets_type={TICKETS_TYPES.SWM}
                    ticketDetails={ticketDetails}
                    selectedTicketType={selectedTicketType}
                    setSelectedTicketType={setSelectedTicketType}
                    selectedIntersectionIds={selectedIntersectionIds}
                    setSelectedIntersectionIds={setSelectedIntersectionIds}
                    totalCount={totalCount}
                    fetchTickets={fetchTickets}
                    enableAction={true}
                    showPagination={true}
                    enableSimulation={true}
                    parentPage="simulations"
                  />
                ),
              },
            ]}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default StimulationFileIndex;